'use strict';

import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import { breakpoints, isDesktop } from '../utilities/breakpoints';

const { processAlertMessage } = require('../accessibility/accessibilityHelper');

/**
 * Swaps lazy-loaded `data-src`/`data-srcset` images to their real source immediately.
 * Only a handful of tiles ever sit in this slider, so eagerly loading all their images
 * up front is cheap and avoids a fetch-on-arrow-click delay while an off-screen slide
 * waits to intersect the viewport. Set directly rather than relying on the site-wide
 * lazyload script, since its init/event timing relative to this bundle isn't guaranteed.
 * @param {HTMLElement} root the element to force-load images within
 */
function forceLoadImages(root) {
    root.querySelectorAll('source[data-srcset]').forEach((source) => {
        source.srcset = source.dataset.srcset;
        source.removeAttribute('data-srcset');
    });

    root.querySelectorAll('img[data-src]').forEach((img) => {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
        img.classList.remove('lazy');
    });
}

/**
 * Turns `rootEl` into a Swiper: wraps `slideColumns` in a `.swiper-wrapper`, appends
 * the prev/pagination/next controls, force-loads their images, and initializes Swiper.
 * @param {HTMLElement} rootEl the element to turn into the Swiper root
 * @param {HTMLElement[]} slideColumns the columns to use as slides
 * @param {Object} [swiperOptions] extra/overriding options merged into the Swiper config
 * @returns {Swiper} the created Swiper instance
 */
function buildSwiper(rootEl, slideColumns, swiperOptions) {
    const wrapperEl = document.createElement('div');
    wrapperEl.className = 'swiper-wrapper';
    slideColumns.forEach((column) => {
        column.classList.add('swiper-slide');
        wrapperEl.appendChild(column);
    });

    rootEl.classList.add('swiper');
    rootEl.appendChild(wrapperEl);
    rootEl.insertAdjacentHTML('beforeend',
        '<div class="product-grid-section__pager">'
        + '<div class="swiper-button-prev" role="button" tabindex="0" aria-label="Previous"></div>'
        + '<div class="swiper-pagination"></div>'
        + '<div class="swiper-button-next" role="button" tabindex="0" aria-label="Next"></div>'
        + '</div>'
    );

    forceLoadImages(rootEl);

    // Pass resolved elements (scoped to this root) rather than selector strings: other
    // carousels on the page (hero, cross-sell, PDP gallery, ...) reuse the same generic
    // .swiper-button-next/-prev/.swiper-pagination classes, and Swiper's string-selector
    // binding can otherwise pick up the wrong instance's controls.
    return new Swiper(rootEl, Object.assign({
        modules: [Navigation, Pagination],
        slidesPerView: 1,
        loop: false,
        // Images are swapped from placeholder to real src just above and finish loading
        // asynchronously; re-measure automatically if that changes any slide's size.
        observer: true,
        observeParents: true,
        navigation: {
            nextEl: rootEl.querySelector('.swiper-button-next'),
            prevEl: rootEl.querySelector('.swiper-button-prev')
        },
        pagination: {
            el: rootEl.querySelector('.swiper-pagination'),
            clickable: true
        },
        on: {
            // The disabled class on the prev/next buttons can otherwise fall out of
            // sync with the actual isBeginning/isEnd state after a slide change; force
            // a re-check so an arrow never stays (or stops) disabled incorrectly.
            slideChangeTransitionEnd(swiper) {
                swiper.navigation.update();
                processAlertMessage('Showing tile ' + (swiper.activeIndex + 1) + ' of ' + slideColumns.length, true);
            }
        }
    }, swiperOptions));
}

/**
 * Desktop-only: the product slider needs `overflow: hidden` to clip its non-active
 * slides, so a `.product-tile__body` positioned outside its own slide via CSS would
 * just get clipped away rather than visibly overlapping push - there's no CSS-only way
 * to render it over push's corner. Instead, move the active slide's body into a shared
 * overlay element that's a direct sibling of push and the slider (so it isn't inside
 * anything with overflow:hidden), and keep it in sync as slides change. Mobile keeps
 * its own separate layout (a full-width panel in normal flow, not an overlap).
 *
 * Re-evaluated on resize/orientation change (e.g. an iPad rotating across the
 * desktop breakpoint) rather than decided once at load: the desktop-only CSS that
 * hides in-slider bodies (`.product-grid-section__slider .product-tile__body`) reacts
 * live to viewport width on its own, but without this the card could be left with no
 * home at all after crossing the breakpoint in either direction.
 * @param {HTMLElement} container the `.product-grid-section__container` element
 * @param {HTMLElement[]} productColumns the product columns used as slides
 * @param {Swiper} swiper the product slider's Swiper instance
 */
function initPushOverlay(container, productColumns, swiper) {
    // An <a>, not a <div>: the body loses its native click-through to the PDP once
    // moved out of its own .product-tile anchor, so the overlay itself becomes the
    // link instead, kept pointed at whichever product is currently shown.
    const overlayEl = document.createElement('a');
    overlayEl.className = 'product-grid-section__push-overlay';
    container.appendChild(overlayEl);

    let homeTile = null;
    let active = false;

    function showBodyForActiveSlide() {
        if (!active) {
            return;
        }

        const body = overlayEl.firstElementChild;
        if (body && homeTile) {
            homeTile.appendChild(body);
        }

        const activeTile = productColumns[swiper.activeIndex].querySelector(':scope > .product-tile');
        const activeBody = activeTile && activeTile.querySelector(':scope > .product-tile__body');
        if (activeBody) {
            homeTile = activeTile;
            overlayEl.href = activeTile.getAttribute('href');
            overlayEl.setAttribute('aria-label', activeTile.getAttribute('aria-label') || '');
            overlayEl.appendChild(activeBody);
        }
    }

    function setActive(shouldBeActive) {
        if (shouldBeActive === active) {
            return;
        }

        active = shouldBeActive;

        if (active) {
            overlayEl.removeAttribute('aria-hidden');
            showBodyForActiveSlide();
        } else {
            // Not just emptied out - without this an href-less link still sitting in
            // the DOM could linger as an invisible clickable area over other content.
            overlayEl.removeAttribute('href');
            overlayEl.setAttribute('aria-hidden', 'true');

            if (homeTile) {
                // Give the body back to its own slide so mobile's in-flow layout works.
                const body = overlayEl.firstElementChild;
                if (body) {
                    homeTile.appendChild(body);
                }
                homeTile = null;
            }
        }
    }

    setActive(isDesktop());
    swiper.on('slideChangeTransitionEnd', showBodyForActiveSlide);
    window.addEventListener('resize', () => setActive(isDesktop()));
}

/**
 * In the 2-tile layout with 3+ configured components, the push tile (identified by its
 * `.push` markup) stays fixed on the left at every breakpoint, and only the product
 * tiles slide, 1 at a time, on the right - matching the reference design where push
 * isn't part of the swipeable set. Each product's info card overlaps push's corner via
 * initPushOverlay rather than the product tile's own space. If no push tile is present
 * (e.g. 3-4 product tiles only), falls back to a single carousel with every tile as a
 * peer slide, 1 visible on mobile and 2 on desktop.
 * @param {HTMLElement} container the `.product-grid-section__container` element
 */
function initSlider(container) {
    const columns = Array.from(container.querySelectorAll(':scope > .product-grid-section__col'));

    if (columns.length < 3) {
        return; // 2 or fewer components already fit the default 2-column layout
    }

    const pushColumn = columns.find((column) => column.querySelector(':scope > .push'));

    if (pushColumn) {
        const productColumns = columns.filter((column) => column !== pushColumn);

        if (productColumns.length < 2) {
            return; // only 1 product besides the fixed push tile - nothing to slide
        }

        const sliderEl = document.createElement('div');
        sliderEl.className = 'product-grid-section__slider';
        container.appendChild(sliderEl);
        const swiper = buildSwiper(sliderEl, productColumns);
        initPushOverlay(container, productColumns, swiper);
        return;
    }

    buildSwiper(container, columns, {
        breakpoints: {
            [breakpoints.desktop]: {
                slidesPerView: 2
            }
        }
    });
}

document.querySelectorAll('.product-grid-section.special-display .product-grid-section__container')
    .forEach(initSlider);

// Mobile background color is BM-configurable (see productGridSection.json), 2-tile
// layout only; the actual mobile-only application lives in CSS
// (@include mobile { background: var(...) }) so this only needs to set the custom
// property, not decide when it's visually used.
document.querySelectorAll('.product-grid-section.tiles-2.special-display[data-mobile-bg]').forEach((section) => {
    if (section.dataset.mobileBg) {
        section.style.setProperty('--product-grid-section-mobile-bg', section.dataset.mobileBg);
    }
});
